import React, { useCallback, useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export const HeroBanner = memo(function HeroBanner() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const slides = [{
    id: 1,
    title: 'MAIN HERO BANNER',
    subtitle: '75% WIDTH (DESKTOP)\n100% WIDTH (MOBILE)',
    description: 'ASPECT RATIO: 21:9',
    bgColor: 'bg-gradient-to-br from-gray-400 via-gray-300 to-gray-200'
  }, {
    id: 2,
    title: 'SPECIAL OFFERS',
    subtitle: 'DISCOVER OUR LATEST\nPRODUCTS & DEALS',
    description: 'ASPECT RATIO: 21:9',
    bgColor: 'bg-gradient-to-br from-blue-300 to-blue-100'
  }, {
    id: 3,
    title: 'SUMMER COLLECTION',
    subtitle: 'NEW ARRIVALS FOR\nTHE SEASON',
    description: 'ASPECT RATIO: 21:9',
    bgColor: 'bg-gradient-to-br from-amber-300 via-yellow-200 to-amber-100'
  }, {
    id: 4,
    title: 'EXCLUSIVE DEALS',
    subtitle: 'LIMITED TIME\nOFFERS',
    description: 'ASPECT RATIO: 21:9',
    bgColor: 'bg-green-200'
  }];
  useEffect(() => {
    const interval = setInterval(() => {
      setDirection(1);
      setCurrentSlide(prev => prev === slides.length - 1 ? 0 : prev + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);
  const goToSlide = useCallback((index: number) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  }, [currentSlide]);
  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentSlide(prev => prev === slides.length - 1 ? 0 : prev + 1);
  }, [slides.length]);
  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrentSlide(prev => prev === 0 ? slides.length - 1 : prev - 1);
  }, [slides.length]);
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0
    })
  };
  return <>
      <div className="container mx-auto px-0 md:px-4 md:pt-6" data-id="element-2379" data-mp="gfrfnr2l9kbq">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-0 md:gap-6" data-id="element-2380" data-mp="bfwqpt2dj4tb">
          {/* Main Hero Banner - Mobile & Desktop Slider */}
          <div className="md:col-span-3 md:rounded-lg overflow-hidden relative -mt-[60px] md:mt-0" data-id="element-2381" data-mp="3jwhu8uc8l5p1">
            {/* Gradient overlay for mobile to improve text visibility and match the blue aesthetic */}
            <div className="absolute inset-0 bg-gradient-to-b from-blue-400/40 via-blue-300/20 to-transparent z-10 md:hidden pointer-events-none" data-id="element-2382" data-mp="3tq8e1lxf4rye"></div>
            <div className="relative w-full overflow-hidden" style={{
            paddingTop: window.innerWidth < 768 ? '133%' : '42.86%'
          }} data-id="element-2383" data-mp="191axirhp5hzo">
              {/* Slider for both mobile and desktop */}
              <AnimatePresence initial={false} custom={direction} data-id="element-2384" data-mp="1glvaajfqkmfb">
                <motion.div key={currentSlide} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                x: {
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                },
                opacity: {
                  duration: 0.2
                }
              }} className={`absolute inset-0 w-full h-full ${slides[currentSlide].bgColor}`} data-id="element-2385" data-mp="1gn6eulh09y7h">
                  <div className="absolute inset-0 flex flex-col justify-end md:justify-center p-6 md:p-8 pt-[var(--header-height-mobile)] md:pt-8 text-gray-800 pb-24 md:pb-8 max-w-lg" data-id="element-2386" data-mp="2dbvu04190cpr">
                    <h2 className="text-4xl sm:text-3xl md:text-4xl font-black mb-2 leading-tight text-white md:text-gray-800 drop-shadow-lg md:drop-shadow-none uppercase tracking-tighter" data-id="element-2387" data-mp="uf28vucfkwgj">
                      {slides[currentSlide].title}
                    </h2>
                    <p className="text-2xl sm:text-xl md:text-2xl font-extrabold mb-2 md:mb-4 leading-tight whitespace-pre-line text-white md:text-gray-800 drop-shadow-lg md:drop-shadow-none uppercase tracking-tight opacity-90" data-id="element-2388" data-mp="kifr5efeiliq">
                      {slides[currentSlide].subtitle}
                    </p>
                    <p className="text-base hidden md:block" data-id="element-2389" data-mp="188a21qg5u00c">
                      {slides[currentSlide].description}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation Arrows - Desktop Only */}
              <div className="hidden md:block" data-id="element-2390" data-mp="1vwtac5z7mcsf">
                <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all" aria-label="Previous slide" data-id="element-2391" data-mp="1r49vl5osdbm1">
                  <ChevronLeftIcon className="w-6 h-6" data-id="element-2392" data-mp="3iqofff8mwdu8" />
                </button>
                <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all" aria-label="Next slide" data-id="element-2393" data-mp="3nsds0zbi8zkf">
                  <ChevronRightIcon className="w-6 h-6" data-id="element-2394" data-mp="6bmh8twjeuxz" />
                </button>
              </div>

              {/* Dots indicator */}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2 z-20" data-id="element-2395" data-mp="fbrcy6sdc4wb">
                {slides.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`h-2.5 w-2.5 rounded-full transition-all ${currentSlide === index ? 'bg-white w-6' : 'bg-white/50'}`} aria-label={`Go to slide ${index + 1}`} style={{
                willChange: 'transform'
              }} data-id="element-2396" data-mp="kb3zeqfbv9yd" data-mpi={index} />)}
              </div>
            </div>
          </div>
          {/* Vertical Banner - Desktop only */}
          <div className="hidden md:block rounded-lg overflow-hidden relative bg-red-500" data-id="element-2397" data-mp="2v2n317lqur1m">
            <div className="relative w-full h-full md:h-auto" style={{
            paddingTop: '100%'
          }} data-id="element-2398" data-mp="2v4zfj58qg97a">
              <div className="absolute inset-0 flex flex-col justify-center items-center p-6 text-white text-center" data-id="element-2399" data-mp="343uouq72pzet">
                <span className="text-sm font-bold bg-white text-red-600 px-3 py-1 rounded-full mb-4" data-id="element-2400" data-mp="1eqhfgmk3tl6u">
                  DIMENSIONS
                </span>
                <h2 className="text-4xl font-bold mb-2" data-id="element-2401" data-mp="3lcdsa4tzhqum">25%</h2>
                <p className="text-lg font-semibold mb-4" data-id="element-2402" data-mp="3qbqm59j9vy6p">
                  WIDTH
                  <br data-id="element-2403" data-mp="3qdte3z3zk1e2" />
                  VERTICAL BANNER
                </p>
                <p className="text-base" data-id="element-2404" data-mp="3bmaxbz3ay2tn">ASPECT RATIO: 1:1</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Vertical Banner - Mobile only, with proper spacing */}
      <div className="md:hidden py-3" data-id="element-2405" data-mp="32kunptkba4qh">
        <div className="container mx-auto px-4" data-id="element-2406" data-mp="37kz6jq8ozbjv">
          <div className="rounded-lg overflow-hidden relative bg-red-500" data-id="element-2407" data-mp="37mttfa3ixdql">
            <div className="relative w-full" style={{
            paddingTop: '100%'
          }} data-id="element-2408" data-mp="2sorjmi5wfw0w">
              <div className="absolute inset-0 flex flex-col justify-center items-center p-6 text-white text-center" data-id="element-2409" data-mp="2xow5knpktiyi">
                <span className="text-sm font-bold bg-white text-red-600 px-3 py-1 rounded-full mb-4" data-id="element-2410" data-mp="vvg22q8e49m4">
                  DIMENSIONS
                </span>
                <h2 className="text-4xl font-bold mb-2" data-id="element-2411" data-mp="3fr7fvknhad65">25%</h2>
                <p className="text-lg font-semibold mb-4" data-id="element-2412" data-mp="3asdy3g15s006">
                  WIDTH
                  <br data-id="element-2413" data-mp="361nuo8cam784" />
                  VERTICAL BANNER
                </p>
                <p className="text-base" data-id="element-2414" data-mp="35zkvaxp931qq">ASPECT RATIO: 1:1</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
});